import { useEffect, useState } from 'react';

/* ── Week 1 availability (from the ALTA site) ──────────────────────────── */

export type Availability = 'in' | 'out' | 'maybe' | 'silent';

export interface RosterEntry {
  name: string;
  role?: string;
  status: Availability;
}

/**
 * When this snapshot was taken off the ALTA availability grid. It is typed in
 * by hand, so say so on the page rather than implying it is live — naming a
 * teammate as a no-show is worth being accurate about.
 */
export const ROSTER_AS_OF = 'September 10';

/** The full 25-man roster and where each stands for Week 1 at Seven Hills. */
export const WEEK_ONE_ROSTER: RosterEntry[] = [
  { name: 'Chuck Giddens', status: 'in' },
  { name: 'Daniel Longacre', role: 'Captain', status: 'in' },
  { name: 'Jon Baker', status: 'in' },
  { name: 'Jeff Bickerton', status: 'in' },
  { name: 'Brian Bonsall', status: 'in' },
  { name: 'Steve Fleming', status: 'in' },
  { name: 'Larry Gillen', status: 'in' },
  { name: 'Stephen Horn, Jr.', status: 'in' },
  { name: 'Sam Pearce', status: 'in' },
  { name: 'Loren Plisco', status: 'in' },
  { name: 'Bill Tisdale', status: 'in' },
  { name: 'Mark Lind', role: 'Co-Captain', status: 'out' },
  { name: 'Chris Burns', status: 'out' },
  { name: 'Randy Greaser', status: 'out' },
  { name: 'Brett Hellenga', status: 'out' },
  { name: 'Mak Kawano', status: 'out' },
  { name: 'Jonathan Sabo', status: 'out' },
  { name: 'Kevin Stark', status: 'out' },
  { name: 'Kenneth Barnes', status: 'maybe' },
  { name: 'Scott Mackie', status: 'maybe' },
  { name: 'Jeff Adams', status: 'silent' },
  { name: 'Greg Mueller', status: 'silent' },
  { name: 'Jon Sabo', status: 'silent' },
  { name: 'Zachary Waters', status: 'silent' },
  { name: 'Mike Wemyss', status: 'silent' },
];

/* ── Live roster feed ──────────────────────────────────────────────────── */

/**
 * The Second Brain Pi scrapes ALTA on a schedule and publishes a token-gated,
 * read-only projection of it. The browser calls our own same-origin
 * /api/tennis, and the App Service does the authenticated call — the Pi's
 * token never reaches a browser. If any of that is down, the page falls back
 * to WEEK_ONE_ROSTER above, so it always renders something true-as-of-then.
 */
export interface TennisFeed {
  roster: RosterEntry[];
  /** When the Pi last scraped ALTA. Empty when we are on the fallback. */
  fetchedAt: string;
  source: 'live' | 'fallback';
}

export function useTennisFeed(): TennisFeed {
  const [feed, setFeed] = useState<TennisFeed>({
    roster: WEEK_ONE_ROSTER,
    fetchedAt: '',
    source: 'fallback',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/tennis');
        if (!res.ok) return; // 503 until the App Service settings exist
        const data = (await res.json()) as {
          roster?: { name?: string; role?: string; status?: string }[];
          fetched_at?: string;
        };
        const roster = (data.roster ?? [])
          .filter((r) => r.name)
          .map<RosterEntry>((r) => ({
            name: r.name as string,
            role: r.role || undefined,
            status: (['in', 'out', 'maybe', 'silent'].includes(r.status ?? '')
              ? r.status
              : 'silent') as Availability,
          }));
        // An empty roster is a broken feed, not an empty team — keep the
        // fallback rather than rendering a page with nobody on it.
        if (!cancelled && roster.length) {
          setFeed({ roster, fetchedAt: data.fetched_at ?? '', source: 'live' });
        }
      } catch {
        /* keep the fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return feed;
}
